import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { StyledLink } from '@src/common/Link';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { event } from '@uhg-abyss/web/tools/event';
import Markdown from 'markdown-to-jsx';

const Wrapper = styled('div', {
  background: 'rgb(255, 255, 255)',
  border: '1px solid rgb(219, 219, 219)',
  boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
  borderRadius: '4px',
  margin: '0',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  height: '400px',

  '@screen >= $xl': {
    width: '100%',
  },
});

const IconBox = styled('div', {
  display: 'flex',
  alignItems: 'center',
  height: '40px',
});

const Title = styled('h3', {
  color: 'rgb(0, 38, 119)',
  fontSize: '24px ! important',
  fontWeight: '600',
  lineHeight: '24px ! important',
  margin: '16px 0 16px 0',
  minHeight: '45px',
});

const Description = styled('p', {
  color: 'rgb(51, 51, 51)',
  fontSize: '15px',
  letterSpacing: '0px',
  lineHeight: '24px',
  margin: '0',
  wordBreak: 'break-word',
  minHeight: '48px',
  marginBottom: '26px',
});

const DescBox = styled('div', {
  minHeight: '48px',
  height: 'auto',
});

const BadgeStyle = styled('div', {
  display: 'flex',
  justifyContent: 'left',
  marginBottom: '5px',
  '.abyss-badge-content': {
    fontSize: '13px',
  },
});

const LinkBox = styled('div', {
  color: 'rgb(25, 110, 207)',
  fontSize: '15px !important',
  fontWeight: 'bold',
  letterSpacing: '0px',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  margin: '16px 0',
  marginTop: 'auto',
  marginBottom: '12px',

  a: {
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      textDecoration: 'none',
    },
  },

  img: {
    marginLeft: '6px',
  },
});

export const APICard = ({
  src,
  title,
  description,
  link,
  linkLabel,
  badge,
  badgeMessage,
  badgeLink,
  onClick,
  index,
  badgeVariant,
  badgeIcon,
}) => {
  const { navigate } = useRouter();
  const onViewApiClicked = (callback, routeLink) => {
    callback();
    navigate(routeLink);
  };
  const badgeEvent = () => {
    const interactionName = title;
    const interactionValue = badgeMessage;
    const interactionContext = 'tile cta clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
    navigate(badgeLink);
  };

  return (
    <Wrapper>
      <IconBox>
        <IconBrand id={`api-${title}-icon`} icon={src} size={40} />
      </IconBox>
      <Title id={`api-${title}`}>{title}</Title>
      {badge && (
        <BadgeStyle>
          <Badge
            icon={
              badgeLink !== null && badgeLink !== '' ? (
                <IconSymbol
                  color={`$${badgeVariant}1`}
                  icon={badgeIcon || ''}
                  size="$sm"
                />
              ) : null
            }
            variant={badgeVariant}
            style={badgeLink ? { cursor: 'pointer' } : {}}
            onClick={
              badgeLink
                ? () => {
                    badgeEvent();
                  }
                : {}
            }
          >
            {badgeMessage}
          </Badge>
        </BadgeStyle>
      )}
      <DescBox>
        <Markdown
          options={{
            overrides: {
              p: Description,
              span: Description,
            },
          }}
        >
          {description}
        </Markdown>
      </DescBox>
      <LinkBox>
        <StyledLink
          onClick={() => {
            onViewApiClicked(onClick, link);
          }}
          fontWeight="$bold"
          size="15px"
          after={<IconSymbol icon="arrow_forward" size="20px" />}
        >
          {linkLabel}
        </StyledLink>
      </LinkBox>
    </Wrapper>
  );
};

APICard.propTypes = {
  src: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  link: PropTypes.string,
  linkLabel: PropTypes.string,
  badge: PropTypes.bool,
  badgeMessage: PropTypes.string,
  badgeLink: PropTypes.string,
  onClick: PropTypes.func,
  index: PropTypes.number.isRequired,
  badgeVariant: PropTypes.string,
  badgeIcon: PropTypes.string,
};

APICard.defaultProps = {
  src: '',
  title: '',
  description: '',
  link: '',
  linkLabel: '',
  badge: false,
  badgeMessage: '',
  badgeLink: '',
  badgeVariant: '',
  badgeIcon: '',
  onClick: () => {},
};
